import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import rateLimiter from '../utils/rateLimiter';

export type ResendConfirmationReason = 'expired' | 'unconfirmed' | 'generic';

interface ResendConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
  reason?: ResendConfirmationReason;
}

const ResendConfirmationModal: React.FC<ResendConfirmationModalProps> = ({
  open,
  onClose,
  initialEmail = '',
  reason = 'generic'
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [cooldownTime, setCooldownTime] = useState(0);
  const { resendConfirmation } = useAuth();
  const { showSuccess } = useToast();

  useEffect(() => {
    if (open) {
      setEmail(initialEmail || '');
      setError('');
      setSuccess(false);
      
      // Check if there's a cooldown for this email
      const rateLimitKey = `resend_${initialEmail}`;
      const remainingCooldown = rateLimiter.getRemainingCooldown(rateLimitKey);
      setCooldownTime(remainingCooldown);
    }
  }, [open, initialEmail]);

  // Update cooldown timer
  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setInterval(() => {
        setCooldownTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [cooldownTime]);

  const getReasonMessage = () => {
    switch (reason) {
      case 'expired':
        return {
          title: 'Confirmation Link Expired',
          message: 'Your confirmation link has expired. We will send you a new one to activate your account.',
          severity: 'warning' as const
        };
      case 'unconfirmed':
        return {
          title: 'Account Not Activated',
          message: 'Your account needs to be activated. Please check your email for the confirmation link, or we can send you a new one.',
          severity: 'info' as const
        };
      default:
        return {
          title: 'Resend Confirmation Email',
          message: 'Enter your email address and we will send you a new confirmation link.',
          severity: 'info' as const
        };
    }
  };

  const reasonInfo = getReasonMessage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await resendConfirmation(email);
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(true);
        showSuccess('Confirmation email sent! Please check your inbox.');
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError('Failed to send confirmation email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLoading(false);
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus={true}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Email color="primary" sx={{ fontSize: '1.5rem' }} />
          <Typography variant="h6" component="div" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {reasonInfo.title}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Confirmation email sent successfully!
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please check your inbox and click the confirmation link to activate your account.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity={reasonInfo.severity} sx={{ mb: 3 }}>
              <Typography variant="body2">
                {reasonInfo.message}
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {cooldownTime > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Please wait {Math.ceil(cooldownTime / 60)} minute{Math.ceil(cooldownTime / 60) !== 1 ? 's' : ''} 
                  ({cooldownTime} seconds) before requesting another confirmation email.
                </Typography>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                required
                disabled={loading || cooldownTime > 0}
                sx={{ mb: 2 }}
                autoFocus
              />
            </form>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!success && (
          <>
            <Button 
              onClick={handleClose} 
              disabled={loading}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { 
                  backgroundColor: 'grey.100' 
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !email || cooldownTime > 0}
              startIcon={loading ? <CircularProgress size={18} /> : <Email />}
              sx={{ 
                minWidth: 140,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {cooldownTime > 0 
                ? `Wait ${cooldownTime}s` 
                : loading 
                  ? 'Sending...' 
                  : 'Send Email'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResendConfirmationModal;
